import '../../styles/restScreens.css';
import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';

// ============================================================
// DependencyPanel — cross-task dependencies (public.task_dependencies)
// Resolving/adding/removing a dependency auto-updates the dependent
// task's status (Blocked <-> In Progress/Queue) via the Postgres
// trigger in migration 002 (Gate 3) — this panel just refetches after
// a change and calls onChanged so the parent InlinePanel can refresh.
// ============================================================

export default function DependencyPanel({ taskId, allTasks = [], editable = true, theme = 'light', onChanged }) {
  const [blockedBy, setBlockedBy] = useState([]); // this task depends on these
  const [blocksOthers, setBlocksOthers] = useState([]); // this task blocks these
  const [loading, setLoading] = useState(true);
  const [pickerTaskId, setPickerTaskId] = useState('');

  const taskNameById = useCallback((id) => {
    const t = allTasks.find(t => t.id === id);
    return t ? t.task_name : '(unknown task)';
  }, [allTasks]);

  const loadDeps = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    const [depOn, depFrom] = await Promise.all([
      supabaseClient.from('task_dependencies').select('*').eq('dependent_task_id', taskId),
      supabaseClient.from('task_dependencies').select('*').eq('blocking_task_id', taskId),
    ]);
    if (!depOn.error) setBlockedBy(depOn.data || []);
    if (!depFrom.error) setBlocksOthers(depFrom.data || []);
    setLoading(false);
  }, [taskId]);

  useEffect(() => { loadDeps(); }, [loadDeps]);

  async function addDependency() {
    if (!pickerTaskId) return;
    const { error } = await supabaseClient
      .from('task_dependencies')
      .insert([{ dependent_task_id: taskId, blocking_task_id: pickerTaskId, dependency_type: 'Finish-to-Start' }]);
    if (!error) {
      setPickerTaskId('');
      await loadDeps();
      onChanged && onChanged();
    }
  }

  async function toggleResolved(dep) {
    const { error } = await supabaseClient
      .from('task_dependencies')
      .update({ resolved: !dep.resolved })
      .eq('id', dep.id);
    if (!error) {
      await loadDeps();
      onChanged && onChanged();
    }
  }

  async function removeDependency(dep) {
    const { error } = await supabaseClient.from('task_dependencies').delete().eq('id', dep.id);
    if (!error) {
      await loadDeps();
      onChanged && onChanged();
    }
  }

  const candidateTasks = allTasks.filter(t =>
    t.id !== taskId && !blockedBy.some(d => d.blocking_task_id === t.id)
  );

  return (
    <div data-m6-layout="tasks" data-m6-component="DependencyPanel" className={`rounded-lg border p-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
      <h4 className={`text-sm font-semibold mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
        Dependencies
      </h4>

      {loading ? (
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
      ) : (
        <>
          <div className="mb-4">
            <div className={`text-xs font-medium mb-2 uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Blocked on
            </div>
            {blockedBy.length === 0 ? (
              <div className={`text-sm italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No blocking dependencies.</div>
            ) : (
              <ul className="space-y-2">
                {blockedBy.map(dep => (
                  <li key={dep.id} className="flex items-center gap-2 group">
                    {editable
                      ? <label className="ia-dependency-check"><input
                        aria-label={`Resolve dependency on ${taskNameById(dep.blocking_task_id)}`}
                        type="checkbox"
                        checked={dep.resolved}
                        onChange={() => toggleResolved(dep)}
                        title="Mark resolved"
                        className="w-4 h-4 accent-[var(--accent-ink)] cursor-pointer"
                      /></label>
                      : <span aria-label={dep.resolved ? 'Resolved' : 'Unresolved'}>{dep.resolved ? '✓' : '○'}</span>}
                    <span className={`flex-1 text-sm ${dep.resolved ? 'line-through text-gray-400' : theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                      {taskNameById(dep.blocking_task_id)}
                    </span>
                    {!dep.resolved && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Blocking</span>
                    )}
                    {editable && (
                      <button
                        onClick={() => removeDependency(dep)}
                        className="opacity-0 group-hover:opacity-100 text-xs text-red-500 hover:text-red-600 transition-opacity"
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {editable && (
            <div className="flex gap-2 mb-4">
              <select
                value={pickerTaskId}
                onChange={(e) => setPickerTaskId(e.target.value)}
                className={`flex-1 text-sm rounded-md border px-2 py-1.5 ${
                  theme === 'dark' ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
                }`}
              >
                <option value="">Add dependency on...</option>
                {candidateTasks.map(t => (
                  <option key={t.id} value={t.id}>{t.task_name}</option>
                ))}
              </select>
              <button
                onClick={addDependency}
                disabled={!pickerTaskId}
                className="text-sm px-3 py-1.5 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
              >
                Add
              </button>
            </div>
          )}

          {blocksOthers.length > 0 && (
            <div>
              <div className={`text-xs font-medium mb-2 uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Blocks
              </div>
              <ul className="space-y-1">
                {blocksOthers.map(dep => (
                  <li key={dep.id} className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    {taskNameById(dep.dependent_task_id)}
                    {!dep.resolved && <span className="ml-2 text-xs text-[var(--ui-moderate)]">(waiting on this task)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
